import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { BellRing, Clock3, Filter, Radar, Settings2, SlidersHorizontal, Zap } from 'lucide-react';
import ModalBase from '@/components/Modal/ModalBase.jsx';
import EmptyState from '@/components/common/EmptyState.jsx';
import Field from '@/components/common/Field.jsx';
import PageHeader from '@/components/common/PageHeader.jsx';
import {
  descartarAlertaOperativa,
  fetchAlertaOperativa,
  fetchDashboardInteligenciaOperativa,
  marcarAlertaComoVista,
  recalcularAlertasOperativas,
  resolverAlertaOperativa,
} from '@/services/inteligenciaOperativa.js';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import GlobalStatusBar from './components/GlobalStatusBar.jsx';
import KpiMiniCard from './components/KpiMiniCard.jsx';
import AlertCard from './components/AlertCard.jsx';
import InsightItem from './components/InsightItem.jsx';
import ActivityTimeline from './components/ActivityTimeline.jsx';
import './inteligencia-operativa.css';

const DEFAULT_FILTERS = {
  desde: '',
  hasta: '',
  sucursalId: '',
  lineaId: '',
  estado: '',
  severidad: '',
  tipo: '',
};

const SEVERITY_WEIGHT = {
  CRITICA: 4,
  ALTA: 3,
  MEDIA: 2,
  BAJA: 1,
};

const KPI_PRIORITY = [
  'guias_demoradas',
  'guias_sin_recepcion',
  'carga_retenida_activa',
  'repartos_pendientes_cierre',
  'entregas_fallidas_48h',
  'valor_declarado_en_transito',
];

const severityClass = {
  CRITICA: 'bg-[rgba(220,38,38,0.14)] text-red-700 dark:text-red-300',
  ALTA: 'bg-[rgba(245,158,11,0.16)] text-amber-700 dark:text-amber-300',
  MEDIA: 'bg-[rgba(234,179,8,0.16)] text-amber-700 dark:text-amber-300',
  BAJA: 'bg-[rgba(var(--brand),0.14)] text-[rgb(var(--brand-700))]',
};

function formatDateTime(value) {
  if (!value) return 'Sin fecha';
  return new Intl.DateTimeFormat('es-AR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value));
}

function formatRelativeTime(value) {
  if (!value) return 'sin referencia';
  const diffMs = Date.now() - new Date(value).getTime();
  const diffMinutes = Math.max(1, Math.round(diffMs / 60000));
  if (diffMinutes < 60) return `+${diffMinutes}m`;
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return `+${diffHours}h`;
  const diffDays = Math.round(diffHours / 24);
  return `+${diffDays}d`;
}

function resolveEntityLink(alerta) {
  if (alerta.entidadTipo === 'GUIA') return `/guias/${alerta.entidadId}`;
  if (alerta.entidadTipo === 'FOJA') return `/fojas/recepcion/${alerta.entidadId}`;
  if (alerta.entidadTipo === 'REPARTO') return `/repartos/${alerta.entidadId}`;
  if (alerta.entidadTipo === 'CHOFER') return `/administracion/choferes/${alerta.entidadId}`;
  if (alerta.entidadTipo === 'SUCURSAL') return '/administracion/empresa/sucursales-de-la-empresa';
  return null;
}

function buildAlertViewModel(alerta) {
  return {
    ...alerta,
    metadata: alerta.metadataJson || alerta.metadata || {},
    relatedLabel: alerta.relatedLabel || `${alerta.entidadTipo} #${alerta.entidadId}`,
    delayLabel: formatRelativeTime(alerta.detectadaEn),
  };
}

function buildActivityItems(alertas, recentActions) {
  const detectedItems = (alertas || []).slice(0, 8).map((alerta) => ({
    id: `detected-${alerta.id}`,
    kind: 'detected',
    message: `${alerta.titulo} detectada en ${alerta.sucursalNombre || 'sucursal sin nombre'}`,
    timestamp: formatDateTime(alerta.detectadaEn),
    sortDate: alerta.detectadaEn,
  }));

  const actionItems = recentActions.map((item) => ({
    ...item,
    sortDate: item.rawDate,
  }));

  return [...actionItems, ...detectedItems]
    .sort((a, b) => new Date(b.sortDate || 0).getTime() - new Date(a.sortDate || 0).getTime())
    .slice(0, 10);
}

function getMetricLabel(metricas, code, fallback) {
  return metricas.find((item) => item.codigo === code)?.etiqueta || fallback;
}

export default function InteligenciaOperativa() {
  const { sucursal } = useAuth();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [error, setError] = useState('');
  const [dashboard, setDashboard] = useState(null);
  const [focusMode, setFocusMode] = useState(false);
  const [selectedAlertId, setSelectedAlertId] = useState(null);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [newAlertIds, setNewAlertIds] = useState([]);
  const [changedMetricCodes, setChangedMetricCodes] = useState([]);
  const [recentActions, setRecentActions] = useState([]);
  const previousAlertIdsRef = useRef([]);
  const previousMetricsRef = useRef({});
  const firstLoadRef = useRef(true);

  const effectiveFilters = useMemo(
    () => ({
      ...filters,
      sucursalId: filters.sucursalId || sucursal?.id || undefined,
      lineaId: filters.lineaId || undefined,
      estado: filters.estado || undefined,
      severidad: filters.severidad || undefined,
      tipo: filters.tipo || undefined,
      desde: filters.desde || undefined,
      hasta: filters.hasta || undefined,
    }),
    [filters, sucursal?.id],
  );

  const filtersKey = JSON.stringify(effectiveFilters);

  const loadDashboard = async (shouldRecalculate = false, silent = false) => {
    if (!silent) setError('');

    try {
      if (shouldRecalculate) {
        setRecalculating(true);
        await recalcularAlertasOperativas(effectiveFilters);
      }

      const data = await fetchDashboardInteligenciaOperativa(effectiveFilters);
      const incomingAlerts = (data?.alertas || []).map(buildAlertViewModel);
      const incomingMetrics = data?.metricas || [];

      if (firstLoadRef.current) {
        previousAlertIdsRef.current = incomingAlerts.map((item) => String(item.id));
        previousMetricsRef.current = Object.fromEntries(incomingMetrics.map((item) => [item.codigo, item.valor]));
        firstLoadRef.current = false;
        setNewAlertIds([]);
        setChangedMetricCodes([]);
      } else {
        const previousAlertIds = new Set(previousAlertIdsRef.current);
        const nextNewIds = incomingAlerts
          .map((item) => String(item.id))
          .filter((id) => !previousAlertIds.has(id));
        setNewAlertIds(nextNewIds);
        previousAlertIdsRef.current = incomingAlerts.map((item) => String(item.id));

        const previousMetrics = previousMetricsRef.current;
        const changedCodes = incomingMetrics
          .filter((item) => previousMetrics[item.codigo] !== item.valor)
          .map((item) => item.codigo);
        setChangedMetricCodes(changedCodes);
        previousMetricsRef.current = Object.fromEntries(incomingMetrics.map((item) => [item.codigo, item.valor]));
      }

      setDashboard({
        ...data,
        alertas: incomingAlerts,
      });
      setLastUpdatedAt(new Date());
    } catch (err) {
      setError(err?.message || 'No se pudo cargar Inteligencia Operativa.');
    } finally {
      setLoading(false);
      setRecalculating(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    loadDashboard(false);
  }, [filtersKey]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      loadDashboard(false, true);
    }, 30000);

    return () => window.clearInterval(intervalId);
  }, [filtersKey]);

  useEffect(() => {
    if (!selectedAlertId) return;
    let active = true;
    setDetailLoading(true);

    fetchAlertaOperativa(selectedAlertId)
      .then((data) => {
        if (!active) return;
        setSelectedAlert({
          ...data,
          metadata: data?.metadataJson || data?.metadata || {},
        });
      })
      .catch((err) => {
        if (!active) return;
        setError(err?.message || 'No se pudo cargar la alerta.');
      })
      .finally(() => {
        if (active) setDetailLoading(false);
      });

    return () => {
      active = false;
    };
  }, [selectedAlertId]);

  const alerts = dashboard?.alertas || [];
  const activeCount = alerts.length;
  const criticalCount = alerts.filter((item) => item.severidad === 'CRITICA').length;
  const visibleAlerts = useMemo(() => {
    const sorted = [...alerts].sort((a, b) => {
      const severityDelta = (SEVERITY_WEIGHT[b.severidad] || 0) - (SEVERITY_WEIGHT[a.severidad] || 0);
      if (severityDelta !== 0) return severityDelta;
      return new Date(a.detectadaEn || 0).getTime() - new Date(b.detectadaEn || 0).getTime();
    });

    if (focusMode) {
      return sorted.filter((item) => item.severidad === 'CRITICA');
    }

    return sorted;
  }, [alerts, focusMode]);

  const metrics = useMemo(() => {
    const available = dashboard?.metricas || [];
    const prioritized = KPI_PRIORITY
      .map((code) => available.find((item) => item.codigo === code))
      .filter(Boolean);

    if (prioritized.length) return prioritized;
    return available.slice(0, 6);
  }, [dashboard?.metricas]);

  const summary = dashboard?.resumenSeveridad || [];
  const insightItems = dashboard?.insights || [];
  const activityItems = useMemo(() => buildActivityItems(alerts, recentActions), [alerts, recentActions]);

  const quickSummary = useMemo(() => {
    const delayed = getMetricLabel(metrics, 'guias_demoradas', 'Demoradas');
    const pendingReception = getMetricLabel(metrics, 'guias_sin_recepcion', 'Sin recepcion');
    return `${delayed}: ${metrics.find((item) => item.codigo === 'guias_demoradas')?.valor || 0} | ${pendingReception}: ${metrics.find((item) => item.codigo === 'guias_sin_recepcion')?.valor || 0}`;
  }, [metrics]);

  const pushRecentAction = (kind, message) => {
    const now = new Date();
    setRecentActions((current) => [
      {
        id: `${kind}-${now.getTime()}`,
        kind,
        message,
        timestamp: formatDateTime(now),
        rawDate: now.toISOString(),
      },
      ...current,
    ].slice(0, 10));
  };

  const executeAction = async (action, id, title) => {
    const comentario = action === 'view' ? null : window.prompt('Comentario opcional', '') || '';

    if (action === 'view') {
      await marcarAlertaComoVista(id);
      pushRecentAction('viewed', `Alerta "${title}" marcada como vista`);
    }

    if (action === 'resolve') {
      await resolverAlertaOperativa(id, comentario);
      pushRecentAction('resolved', `Alerta "${title}" resuelta`);
    }

    if (action === 'discard') {
      await descartarAlertaOperativa(id, comentario);
      pushRecentAction('pending', `Alerta "${title}" descartada`);
    }

    if (selectedAlertId === id) {
      const data = await fetchAlertaOperativa(id);
      setSelectedAlert({
        ...data,
        metadata: data?.metadataJson || data?.metadata || {},
      });
    }

    await loadDashboard(false, true);
  };

  return (
    <div className={`control-center mx-auto w-full px-3 py-3 sm:px-4 ${focusMode ? 'focus-shell' : 'max-w-[1400px]'}`}>
      <div className="space-y-4">
        <GlobalStatusBar
          criticalCount={criticalCount}
          activeCount={activeCount}
          lastUpdated={lastUpdatedAt ? formatDateTime(lastUpdatedAt) : null}
          recalculating={recalculating}
          focusMode={focusMode}
          onRecalculate={() => loadDashboard(true)}
          onToggleFocus={() => setFocusMode((current) => !current)}
        />

        <PageHeader
          title="Inteligencia Operativa"
          description="Centro de control logistico en vivo con prioridades visibles, acciones rapidas e insights sobre la operacion."
          breadcrumbs={[
            { label: 'Operaciones', href: '/monitor/operaciones' },
            { label: 'Inteligencia Operativa' },
          ]}
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex items-center gap-2 rounded-xl border border-[rgba(var(--border),0.72)] bg-[rgba(var(--panel),0.9)] px-3 py-2 text-sm text-[rgba(var(--muted),0.96)]">
                <Radar className="h-4 w-4 text-[rgb(var(--brand))]" />
                {quickSummary}
              </div>
              <Link
                to="/operaciones/inteligencia/configuracion"
                className="inline-flex items-center gap-2 rounded-xl border border-[rgba(var(--border),0.8)] bg-[rgba(var(--panel),0.9)] px-3 py-2 text-sm font-semibold text-[rgb(var(--fg))]"
              >
                <Settings2 className="h-4 w-4" />
                Configuracion
              </Link>
            </div>
          }
        />

        {!focusMode ? (
          <section className="rounded-[1.4rem] border border-[rgba(var(--border),0.72)] bg-[linear-gradient(135deg,rgba(var(--panel),0.98),rgba(var(--subpanel),0.94))] p-3 shadow-[0_18px_36px_rgba(var(--shadow),0.09)]">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[rgba(var(--muted),0.92)]">Indicadores rapidos</p>
                <h2 className="mt-1 text-lg font-semibold text-[rgb(var(--fg))]">Pulso operativo actual</h2>
              </div>
              <div className="inline-flex items-center gap-2 rounded-full bg-[rgba(var(--fg),0.04)] px-3 py-1.5 text-xs font-medium text-[rgba(var(--muted),0.96)]">
                <BellRing className="h-3.5 w-3.5 text-[rgb(var(--brand))]" />
                {summary.reduce((acc, item) => acc + Number(item.total || 0), 0)} alertas en total
              </div>
            </div>
            <div className="kpi-scroll flex gap-3 overflow-x-auto pb-1">
              {metrics.map((item) => (
                <KpiMiniCard key={item.codigo} item={item} changed={changedMetricCodes.includes(item.codigo)} />
              ))}
            </div>
          </section>
        ) : null}

        {!focusMode ? (
          <section className="rounded-[1.35rem] border border-[rgba(var(--border),0.72)] bg-[rgba(var(--panel),0.96)] p-3 shadow-[0_18px_34px_rgba(var(--shadow),0.08)]">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-[rgb(var(--brand))]" />
                <h2 className="text-base font-semibold text-[rgb(var(--fg))]">Centro de filtros</h2>
              </div>
              <button
                type="button"
                onClick={() => setFilters(DEFAULT_FILTERS)}
                className="rounded-xl border border-[rgba(var(--border),0.78)] bg-[rgba(var(--subpanel),0.9)] px-3 py-2 text-sm font-semibold text-[rgb(var(--fg))]"
              >
                Limpiar filtros
              </button>
            </div>

            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-6 md:col-span-3 xl:col-span-2">
                <Field label="Desde">
                  <input
                    type="date"
                    value={filters.desde}
                    onChange={(e) => setFilters((prev) => ({ ...prev, desde: e.target.value }))}
                    className="w-full rounded-xl border border-[rgba(var(--border),0.72)] bg-[rgb(var(--subpanel))] px-3 py-2"
                  />
                </Field>
              </div>
              <div className="col-span-6 md:col-span-3 xl:col-span-2">
                <Field label="Hasta">
                  <input
                    type="date"
                    value={filters.hasta}
                    onChange={(e) => setFilters((prev) => ({ ...prev, hasta: e.target.value }))}
                    className="w-full rounded-xl border border-[rgba(var(--border),0.72)] bg-[rgb(var(--subpanel))] px-3 py-2"
                  />
                </Field>
              </div>
              <div className="col-span-6 md:col-span-3 xl:col-span-2">
                <Field label="Sucursal">
                  <select
                    value={filters.sucursalId}
                    onChange={(e) => setFilters((prev) => ({ ...prev, sucursalId: e.target.value }))}
                    className="w-full rounded-xl border border-[rgba(var(--border),0.72)] bg-[rgb(var(--subpanel))] px-3 py-2"
                  >
                    <option value="">Todas</option>
                    {(dashboard?.sucursales || []).map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.nombre}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <div className="col-span-6 md:col-span-3 xl:col-span-2">
                <Field label="Linea">
                  <select
                    value={filters.lineaId}
                    onChange={(e) => setFilters((prev) => ({ ...prev, lineaId: e.target.value }))}
                    className="w-full rounded-xl border border-[rgba(var(--border),0.72)] bg-[rgb(var(--subpanel))] px-3 py-2"
                  >
                    <option value="">Todas</option>
                    {(dashboard?.lineas || []).map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.codigo ? `${item.codigo} - ` : ''}
                        {item.nombre}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <div className="col-span-6 md:col-span-3 xl:col-span-2">
                <Field label="Estado">
                  <select
                    value={filters.estado}
                    onChange={(e) => setFilters((prev) => ({ ...prev, estado: e.target.value }))}
                    className="w-full rounded-xl border border-[rgba(var(--border),0.72)] bg-[rgb(var(--subpanel))] px-3 py-2"
                  >
                    <option value="">Todos</option>
                    <option value="NUEVA">Nueva</option>
                    <option value="VISTA">Vista</option>
                    <option value="RESUELTA">Resuelta</option>
                    <option value="DESCARTADA">Descartada</option>
                  </select>
                </Field>
              </div>
              <div className="col-span-6 md:col-span-3 xl:col-span-2">
                <Field label="Severidad">
                  <select
                    value={filters.severidad}
                    onChange={(e) => setFilters((prev) => ({ ...prev, severidad: e.target.value }))}
                    className="w-full rounded-xl border border-[rgba(var(--border),0.72)] bg-[rgb(var(--subpanel))] px-3 py-2"
                  >
                    <option value="">Todas</option>
                    <option value="CRITICA">Critica</option>
                    <option value="ALTA">Alta</option>
                    <option value="MEDIA">Media</option>
                    <option value="BAJA">Baja</option>
                  </select>
                </Field>
              </div>
              <div className="col-span-12">
                <Field label="Tipo de alerta">
                  <input
                    value={filters.tipo}
                    onChange={(e) => setFilters((prev) => ({ ...prev, tipo: e.target.value }))}
                    placeholder="foja, reparto, chofer, sin recepcion"
                    className="w-full rounded-xl border border-[rgba(var(--border),0.72)] bg-[rgb(var(--subpanel))] px-3 py-2"
                  />
                </Field>
              </div>
            </div>
          </section>
        ) : (
          <section className="rounded-[1.35rem] border border-[rgba(var(--border),0.72)] bg-[rgba(var(--panel),0.96)] p-3 shadow-[0_18px_34px_rgba(var(--shadow),0.08)]">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[rgba(var(--muted),0.92)]">Modo foco</p>
                <h2 className="mt-1 text-lg font-semibold text-[rgb(var(--fg))]">Solo criticidad y respuesta inmediata</h2>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => loadDashboard(true)}
                  className="rounded-xl bg-[rgb(var(--brand))] px-3 py-2 text-sm font-semibold text-white"
                >
                  Recalcular ahora
                </button>
                <Link
                  to="/operaciones/inteligencia/configuracion"
                  className="inline-flex items-center gap-2 rounded-xl border border-[rgba(var(--border),0.8)] bg-[rgba(var(--subpanel),0.9)] px-3 py-2 text-sm font-semibold text-[rgb(var(--fg))]"
                >
                  <SlidersHorizontal className="h-4 w-4" />
                  Reglas
                </Link>
              </div>
            </div>
          </section>
        )}

        <section className="grid grid-cols-12 gap-4">
          <div className="col-span-12 xl:col-span-8">
            <section className="rounded-[1.5rem] border border-[rgba(var(--border),0.72)] bg-[linear-gradient(180deg,rgba(var(--panel),0.99),rgba(var(--subpanel),0.97))] p-3 shadow-[0_20px_42px_rgba(var(--shadow),0.1)] sm:p-4">
              <div className="flex flex-col gap-3 border-b border-[rgba(var(--border),0.6)] pb-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[rgba(var(--muted),0.92)]">Panel critico</p>
                  <h2 className="mt-1 text-xl font-semibold tracking-tight text-[rgb(var(--fg))]">
                    {focusMode ? 'Alertas criticas en primer plano' : 'Alertas que requieren decision ya'}
                  </h2>
                  <p className="mt-1 text-sm text-[rgba(var(--muted),0.96)]">
                    Ordenadas por severidad y antiguedad para que el operador vea primero lo mas urgente.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {summary.map((item) => (
                    <div
                      key={item.severidad}
                      className={`rounded-full px-2.5 py-1 text-xs font-semibold ${severityClass[item.severidad] || severityClass.MEDIA}`}
                    >
                      {item.severidad}: {item.total}
                    </div>
                  ))}
                </div>
              </div>

              {loading ? <p className="mt-6 text-sm text-[rgba(var(--muted),0.92)]">Cargando torre de control...</p> : null}
              {error ? <p className="mt-4 rounded-xl bg-[rgba(220,38,38,0.1)] px-3 py-2 text-sm text-red-600">{error}</p> : null}

              {!loading && !visibleAlerts.length ? (
                <div className="mt-4">
                  <EmptyState
                    title={focusMode ? 'Sin alertas criticas activas' : 'Sin alertas para esta vista'}
                    description={focusMode ? 'No hay criticidad maxima en este momento.' : 'Ajusta filtros o recalcula para reevaluar la operacion actual.'}
                  />
                </div>
              ) : null}

              <div className="mt-4 space-y-3">
                {visibleAlerts.map((alerta) => (
                  <AlertCard
                    key={alerta.id}
                    alerta={{
                      ...alerta,
                      metadata: alerta.metadata,
                      relatedLabel: alerta.relatedLabel,
                      descripcion: alerta.descripcion || 'Sin descripcion operativa.',
                      metadataJson: alerta.metadata,
                    }}
                    entityLink={resolveEntityLink(alerta)}
                    isNew={newAlertIds.includes(String(alerta.id))}
                    onOpen={() => setSelectedAlertId(String(alerta.id))}
                    onResolve={() => executeAction('resolve', String(alerta.id), alerta.titulo)}
                  />
                ))}
              </div>
            </section>
          </div>

          {!focusMode ? (
            <div className="col-span-12 xl:col-span-4">
              <div className="space-y-4 xl:sticky xl:top-28">
                <section className="rounded-[1.4rem] border border-[rgba(var(--border),0.72)] bg-[rgba(var(--panel),0.97)] p-4 shadow-[0_18px_36px_rgba(var(--shadow),0.08)]">
                  <div className="flex items-center gap-2">
                    <Zap className="h-4 w-4 text-[rgb(var(--brand))]" />
                    <h2 className="text-base font-semibold text-[rgb(var(--fg))]">Insights operativos</h2>
                  </div>
                  <div className="mt-4 space-y-3">
                    {insightItems.length ? insightItems.map((item) => <InsightItem key={item.codigo || item.titulo} item={item} />) : (
                      <div className="rounded-2xl border border-[rgba(var(--border),0.68)] bg-[rgba(var(--subpanel),0.92)] px-3 py-4 text-sm text-[rgba(var(--muted),0.95)]">
                        Sin sugerencias para esta combinacion de filtros.
                      </div>
                    )}
                  </div>
                </section>

                <section className="rounded-[1.4rem] border border-[rgba(var(--border),0.72)] bg-[rgba(var(--panel),0.97)] p-4 shadow-[0_18px_36px_rgba(var(--shadow),0.08)]">
                  <div className="flex items-center gap-2">
                    <Radar className="h-4 w-4 text-[rgb(var(--brand))]" />
                    <h2 className="text-base font-semibold text-[rgb(var(--fg))]">Concentracion de incidencias</h2>
                  </div>
                  <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-1">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[rgba(var(--muted),0.92)]">Sucursales</p>
                      <div className="mt-2 space-y-2">
                        {(dashboard?.rankingSucursales || []).map((item, index) => (
                          <div key={`${item.id}-${index}`} className="flex items-center justify-between rounded-xl border border-[rgba(var(--border),0.62)] bg-[rgba(var(--subpanel),0.9)] px-3 py-2 text-sm">
                            <span className="truncate pr-3">{item.nombre}</span>
                            <strong>{item.total}</strong>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[rgba(var(--muted),0.92)]">Choferes</p>
                      <div className="mt-2 space-y-2">
                        {(dashboard?.rankingChoferes || []).map((item, index) => (
                          <div key={`${item.id}-${index}`} className="flex items-center justify-between rounded-xl border border-[rgba(var(--border),0.62)] bg-[rgba(var(--subpanel),0.9)] px-3 py-2 text-sm">
                            <span className="truncate pr-3">{item.nombre}</span>
                            <strong>{item.total}</strong>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          ) : null}
        </section>

        {!focusMode ? (
          <section className="rounded-[1.45rem] border border-[rgba(var(--border),0.72)] bg-[rgba(var(--panel),0.97)] p-4 shadow-[0_18px_34px_rgba(var(--shadow),0.08)]">
            <div className="mb-4 flex items-center gap-2">
              <Clock3 className="h-4 w-4 text-[rgb(var(--brand))]" />
              <div>
                <h2 className="text-base font-semibold text-[rgb(var(--fg))]">Actividad reciente</h2>
                <p className="text-sm text-[rgba(var(--muted),0.94)]">Detecciones nuevas, acciones del operador y movimientos relevantes del tablero.</p>
              </div>
            </div>
            {activityItems.length ? (
              <ActivityTimeline items={activityItems} />
            ) : (
              <EmptyState title="Sin actividad reciente" description="Cuando aparezcan alertas o se tomen acciones, se veran aqui." />
            )}
          </section>
        ) : null}

        <ModalBase
          isOpen={Boolean(selectedAlertId)}
          onClose={() => {
            setSelectedAlertId(null);
            setSelectedAlert(null);
          }}
          labelledBy="alerta-operativa-detalle"
          viewportClassName="drawer-viewport"
          className="drawer-panel overflow-hidden border border-[rgba(var(--border),0.7)] bg-[rgb(var(--panel))] p-0 shadow-[0_24px_50px_rgba(var(--shadow),0.18)]"
        >
          <div className="border-b border-[rgba(var(--border),0.7)] bg-[linear-gradient(135deg,rgba(var(--panel),0.98),rgba(var(--subpanel),0.96))] px-5 py-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[rgba(var(--muted),0.92)]">Respuesta operativa</p>
            <h2 id="alerta-operativa-detalle" className="mt-1 text-lg font-semibold text-[rgb(var(--fg))]">Detalle de alerta</h2>
            <p className="mt-1 text-sm text-[rgba(var(--muted),0.94)]">Motivo, historial, regla disparadora y acciones inmediatas.</p>
          </div>

          <div className="max-h-[82vh] overflow-auto bg-[rgb(var(--panel))] px-5 py-5">
            {detailLoading || !selectedAlert ? (
              <p className="text-sm text-[rgba(var(--muted),0.92)]">Cargando detalle...</p>
            ) : (
              <div className="space-y-5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${severityClass[selectedAlert.severidad] || severityClass.MEDIA}`}>
                    {selectedAlert.severidad}
                  </span>
                  <span className="rounded-full bg-[rgba(var(--brand),0.12)] px-2.5 py-1 text-xs font-semibold text-[rgb(var(--brand-700))]">
                    {selectedAlert.estado}
                  </span>
                  <span className="rounded-full bg-[rgba(var(--fg),0.05)] px-2.5 py-1 text-xs font-semibold text-[rgba(var(--muted),0.94)]">
                    {selectedAlert.tipo}
                  </span>
                </div>

                <div>
                  <h3 className="text-xl font-semibold tracking-tight text-[rgb(var(--fg))]">{selectedAlert.titulo}</h3>
                  <p className="mt-2 text-sm leading-6 text-[rgba(var(--muted),0.96)]">{selectedAlert.descripcion}</p>
                </div>

                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-12 rounded-2xl border border-[rgba(var(--border),0.66)] bg-[rgba(var(--subpanel),0.9)] p-4 md:col-span-6">
                    <h4 className="text-sm font-semibold text-[rgb(var(--fg))]">Motivo exacto</h4>
                    <div className="mt-3 space-y-2 text-sm text-[rgba(var(--muted),0.96)]">
                      <p>Sucursal: <strong className="text-[rgb(var(--fg))]">{selectedAlert.sucursalNombre || 'N/D'}</strong></p>
                      <p>Entidad: <strong className="text-[rgb(var(--fg))]">{selectedAlert.relatedLabel || `${selectedAlert.entidadTipo} #${selectedAlert.entidadId}`}</strong></p>
                      <p>Detectada: <strong className="text-[rgb(var(--fg))]">{formatDateTime(selectedAlert.detectadaEn)}</strong></p>
                      <p>Regla: <strong className="text-[rgb(var(--fg))]">{selectedAlert.reglaCodigo || 'Sin codigo'}</strong></p>
                    </div>
                  </div>

                  <div className="col-span-12 rounded-2xl border border-[rgba(var(--border),0.66)] bg-[rgba(var(--subpanel),0.9)] p-4 md:col-span-6">
                    <h4 className="text-sm font-semibold text-[rgb(var(--fg))]">Recomendaciones</h4>
                    <ul className="mt-3 space-y-2 text-sm text-[rgba(var(--muted),0.96)]">
                      {(selectedAlert.recomendaciones || []).length ? (
                        (selectedAlert.recomendaciones || []).map((item, index) => (
                          <li key={`${item}-${index}`}>- {item}</li>
                        ))
                      ) : (
                        <li>- Revisar entidad relacionada y confirmar si la condicion sigue vigente.</li>
                      )}
                    </ul>
                  </div>
                </div>

                <div className="rounded-2xl border border-[rgba(var(--border),0.66)] bg-[rgba(var(--subpanel),0.9)] p-4">
                  <h4 className="text-sm font-semibold text-[rgb(var(--fg))]">Historial breve</h4>
                  <div className="mt-3 space-y-2">
                    {(selectedAlert.historial || []).length ? (
                      (selectedAlert.historial || []).map((item) => (
                        <div key={item.id} className="rounded-xl border border-[rgba(var(--border),0.66)] bg-[rgba(var(--panel),0.96)] px-3 py-3 text-sm">
                          <strong className="text-[rgb(var(--fg))]">{item.estadoNuevo}</strong> - {formatDateTime(item.createdAt)}
                          {item.comentario ? <p className="mt-1 text-[rgba(var(--muted),0.96)]">{item.comentario}</p> : null}
                        </div>
                      ))
                    ) : (
                      <div className="rounded-xl border border-[rgba(var(--border),0.66)] bg-[rgba(var(--panel),0.96)] px-3 py-3 text-sm text-[rgba(var(--muted),0.96)]">
                        No hay historial adicional para esta alerta.
                      </div>
                    )}
                  </div>
                </div>

                <div className="sticky bottom-0 -mx-5 border-t border-[rgba(var(--border),0.66)] bg-[rgba(var(--panel),0.98)] px-5 py-4 backdrop-blur">
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => executeAction('view', String(selectedAlert.id), selectedAlert.titulo)}
                      className="rounded-xl border border-[rgba(var(--border),0.8)] bg-[rgba(var(--subpanel),0.9)] px-3 py-2 text-sm font-semibold text-[rgb(var(--fg))]"
                    >
                      Marcar vista
                    </button>
                    <button
                      type="button"
                      onClick={() => executeAction('resolve', String(selectedAlert.id), selectedAlert.titulo)}
                      className="rounded-xl bg-[rgb(var(--brand))] px-3 py-2 text-sm font-semibold text-white"
                    >
                      Resolver
                    </button>
                    <button
                      type="button"
                      onClick={() => executeAction('discard', String(selectedAlert.id), selectedAlert.titulo)}
                      className="rounded-xl border border-[rgba(var(--border),0.8)] bg-[rgba(var(--subpanel),0.9)] px-3 py-2 text-sm font-semibold text-[rgb(var(--fg))]"
                    >
                      Descartar
                    </button>
                    {resolveEntityLink(selectedAlert) ? (
                      <Link
                        to={resolveEntityLink(selectedAlert) || '#'}
                        className="rounded-xl border border-[rgba(var(--border),0.8)] bg-[rgba(var(--subpanel),0.9)] px-3 py-2 text-sm font-semibold text-[rgb(var(--fg))]"
                      >
                        Ir relacionado
                      </Link>
                    ) : null}
                  </div>
                </div>
              </div>
            )}
          </div>
        </ModalBase>
      </div>
    </div>
  );
}
